import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/use-current-user';
import { apiClient } from '@/utils/api-client';
import { revokeProximaApiKeysById } from '@/services/proxima-project-api-keys';
import type { ApiKeyResource } from '@/types/external-api-keys';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  FieldLabel,
  Input,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from 'prosci-ui';
import { Copy, Eye, EyeSlash, Key, MoreHorizontal, Plus, Search, Trash2, X } from '@/lib/icons';

const PAGE_SIZE = 10;

export interface CreateKeyVariantPayload {
  name: string;
  external_id?: string | null;
  project_id?: number;
}

export interface OwnerProject {
  id: number;
  name: string;
}

export interface CreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (variantPayload: CreateKeyVariantPayload, label: string) => void;
  isCreating: boolean;
  organizationId: number;
  ownerProjects: OwnerProject[];
  ownerProjectsLoading: boolean;
  takenProjectIds: number[];
}

export interface ApiKeysVariantConfig {
  keyType: 'organization' | 'project';
  emptyTitle: string;
  emptyDescription: string;
  addButtonLabel: string;
  sectionTitle: string;
  sectionDescription: string;
  tableHeader: string;
  toastCreated: string;
  toastCreateError: string;
  toastRevoked: string;
  toastRevokeError: string;
  toastBulkRevoked: string;
  toastBulkRevokeError: string;
  CreateDialog: ComponentType<CreateDialogProps>;
  showProjectColumn?: boolean;
  fetchOwnerProjects?: (organizationId: number) => Promise<OwnerProject[]>;
}

interface ProximaApiKeysTabProps {
  config: ApiKeysVariantConfig;
}

export function ProximaApiKeysTab({ config }: ProximaApiKeysTabProps) {
  const { data: currentAdmin } = useCurrentUser();
  const queryClient = useQueryClient();
  const organizationId = currentAdmin.organization_id;

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState('');
  const [createdKeyLabel, setCreatedKeyLabel] = useState('');
  const [keyRevealVisible, setKeyRevealVisible] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkRevokeDialog, setShowBulkRevokeDialog] = useState(false);
  const [rowToRevoke, setRowToRevoke] = useState<ApiKeyResource | null>(null);

  const queryKey = [`proxima-${config.keyType}-api-keys`, organizationId];

  const { data: rawKeys, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      try {
        const response = await apiClient.get<{ data: ApiKeyResource[] }>(
          '/api/external_api/proxima_keys',
          { organization_id: organizationId, key_type: config.keyType }
        );
        return response.data || [];
      } catch {
        toast.error('Something went wrong while fetching API keys.');
        return [];
      }
    },
    enabled: !!organizationId,
    staleTime: 30000,
  });

  const { data: ownerProjects = [], isLoading: ownerProjectsLoading } = useQuery({
    queryKey: ['owner-projects', organizationId],
    queryFn: () => config.fetchOwnerProjects!(organizationId),
    enabled: !!config.fetchOwnerProjects && !!organizationId,
    staleTime: 60000,
  });

  const keys = useMemo(() => rawKeys || [], [rawKeys]);

  const takenProjectIds = useMemo(
    () => keys.map((k) => k.attributes.project_id).filter((id): id is number => id !== null),
    [keys]
  );

  const filteredKeys = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return keys;
    return keys.filter((k) => k.attributes.name?.toLowerCase().includes(term));
  }, [keys, search]);

  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [keys.length, organizationId]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const totalPages = Math.max(1, Math.ceil(filteredKeys.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageSlice = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredKeys.slice(start, start + PAGE_SIZE);
  }, [filteredKeys, safePage]);

  const pageIds = useMemo(() => new Set(pageSlice.map((k) => k.id)), [pageSlice]);
  const allOnPageSelected = pageSlice.length > 0 && pageSlice.every((k) => selectedIds.has(k.id));
  const someOnPageSelected = pageSlice.some((k) => selectedIds.has(k.id));

  const createMutation = useMutation({
    mutationFn: async ({
      variantPayload,
    }: {
      variantPayload: CreateKeyVariantPayload;
      label: string;
    }) => {
      const response = await apiClient.post<{ data: ApiKeyResource }>(
        '/api/external_api/proxima_keys',
        { organization_id: organizationId, key_type: config.keyType, ...variantPayload }
      );
      return response.data;
    },
    onSuccess: (data, { label }) => {
      queryClient.invalidateQueries({ queryKey });
      setCreatedKeyLabel(label);
      setKeyRevealVisible(false);
      setNewlyCreatedKey(data.attributes.key);
      setShowCreateDialog(false);
      toast.success(config.toastCreated);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : config.toastCreateError);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await revokeProximaApiKeysById([Number(id)], organizationId, config.keyType);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success(config.toastRevoked);
    },
    onError: () => {
      toast.error(config.toastRevokeError);
    },
  });

  const bulkRevokeMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await revokeProximaApiKeysById(ids.map(Number), organizationId, config.keyType);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setSelectedIds(new Set());
      setShowBulkRevokeDialog(false);
      toast.success(config.toastBulkRevoked);
    },
    onError: () => {
      toast.error(config.toastBulkRevokeError);
    },
  });

  const toggleSelectAllOnPage = () => {
    if (allOnPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCreate = (variantPayload: CreateKeyVariantPayload, label: string) => {
    createMutation.mutate({ variantPayload, label });
  };

  const selectedCount = selectedIds.size;
  const rangeStart = filteredKeys.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, filteredKeys.length);

  const visiblePages = useMemo(() => {
    if (totalPages <= 5)
      return Array.from({ length: totalPages }, (_, i) => i + 1) as (number | 'ellipsis')[];
    const pages: (number | 'ellipsis')[] = [1];
    const left = Math.max(2, safePage - 1);
    const right = Math.min(totalPages - 1, safePage + 1);
    if (left > 2) pages.push('ellipsis');
    for (let p = left; p <= right; p++) pages.push(p);
    if (right < totalPages - 1) pages.push('ellipsis');
    pages.push(totalPages);
    return pages;
  }, [totalPages, safePage]);

  return (
    <div className="space-y-6">
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size="large" />
        </div>
      ) : keys.length === 0 ? (
        <div className="flex flex-col items-center">
          <img
            src="/api-keys-not-found.svg"
            alt={config.emptyTitle}
            className="mb-8"
            width={320}
            height={217}
            decoding="async"
          />
          <h2 className="text-center text-xl font-bold text-foreground sm:text-2xl">
            {config.emptyTitle}
          </h2>
          <p className="mt-3 max-w-md text-center text-base leading-6 text-muted-foreground">
            {config.emptyDescription}
          </p>
          <Button
            type="button"
            className="mt-8 bg-teal-900 text-white hover:bg-emerald-800 focus-visible:ring-emerald-600"
            onClick={() => setShowCreateDialog(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            {config.addButtonLabel}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex justify-between gap-20">
            <div className="flex flex-1 flex-col items-start">
              <h2 className="text-xl font-bold text-foreground sm:text-2xl">{config.sectionTitle}</h2>
              <p className="mt-3 text-base leading-6 text-muted-foreground">
                {config.sectionDescription}
              </p>
            </div>
          </div>

          <div
            className={`flex items-center gap-3 transition-[opacity,visibility] duration-150 ${
              selectedCount > 0 ? 'visible opacity-100' : 'invisible pointer-events-none opacity-0'
            }`}
            aria-hidden={selectedCount === 0}
          >
            <span className="text-sm text-muted-foreground">
              {selectedCount} key{selectedCount !== 1 ? 's' : ''} selected
            </span>
            <div className="inline-flex items-center rounded-md border border-input shadow-xs">
              <Button
                variant="ghost"
                size="sm"
                className="rounded-r-none border-r border-input hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setShowBulkRevokeDialog(true)}
                disabled={bulkRevokeMutation.isPending}
              >
                <Trash2 className="mr-1.5 h-4 w-4" />
                Revoke keys
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="rounded-l-none"
                onClick={() => setSelectedIds(new Set())}
              >
                <X className="mr-1.5 h-4 w-4" />
                Clear selection
              </Button>
            </div>
          </div>

          <div className="flex justify-between">
            <div className="relative w-1/2">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by key name"
                className="pl-9"
              />
              {search && (
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4 cursor-pointer" />
                </button>
              )}
            </div>
            <Button
              type="button"
              className="bg-teal-900 text-white hover:bg-emerald-800 focus-visible:ring-emerald-600"
              onClick={() => setShowCreateDialog(true)}
            >
              <Plus className="mr-2 h-4 w-4" />
              {config.addButtonLabel}
            </Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={allOnPageSelected ? true : someOnPageSelected ? 'indeterminate' : false}
                    onCheckedChange={() => toggleSelectAllOnPage()}
                    aria-label="Select all on this page"
                    className="data-[state=checked]:bg-teal-900 data-[state=checked]:border-teal-900 data-[state=indeterminate]:bg-teal-900 data-[state=indeterminate]:border-teal-900"
                  />
                </TableHead>
                <TableHead>{config.tableHeader}</TableHead>
                {config.showProjectColumn && <TableHead>Project</TableHead>}
                <TableHead>API key</TableHead>
                <TableHead className="w-12 text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageSlice.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={config.showProjectColumn ? 5 : 4}
                    className="py-20 text-center text-sm text-muted-foreground"
                  >
                    No keys match your search.
                  </TableCell>
                </TableRow>
              )}
              {pageSlice.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.has(row.id)}
                      onCheckedChange={() => toggleRow(row.id)}
                      aria-label={`Select ${row.attributes.name}`}
                      className="data-[state=checked]:bg-teal-900 data-[state=checked]:border-teal-900 data-[state=indeterminate]:bg-teal-900 data-[state=indeterminate]:border-teal-900"
                    />
                  </TableCell>
                  <TableCell className="font-medium">{row.attributes.name}</TableCell>
                  {config.showProjectColumn && (
                    <TableCell>
                      {ownerProjects.find((p) => p.id === row.attributes.project_id)?.name ?? '—'}
                    </TableCell>
                  )}
                  <TableCell>
                    <Badge className="bg-[#F0F0F0] font-mono text-base text-neutral-600">
                      {row.attributes.masked_key}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" aria-label="Row actions">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => setRowToRevoke(row)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Revoke key
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="flex items-center justify-between border-t border-border pt-3">
            <p className="text-sm text-muted-foreground">
              {rangeStart}–{rangeEnd} of {filteredKeys.length}
              {search.trim() && filteredKeys.length !== keys.length && (
                <span className="ml-1 text-muted-foreground/70">(filtered from {keys.length})</span>
              )}
            </p>
            <Pagination className="mx-0 w-auto">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((p) => Math.max(1, p - 1));
                    }}
                    className={safePage <= 1 ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
                {visiblePages.map((p, idx) =>
                  p === 'ellipsis' ? (
                    <PaginationItem key={`ellipsis-${idx}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  ) : (
                    <PaginationItem key={p}>
                      <PaginationLink
                        href="#"
                        isActive={p === safePage}
                        onClick={(e) => {
                          e.preventDefault();
                          setPage(p);
                        }}
                      >
                        {p}
                      </PaginationLink>
                    </PaginationItem>
                  )
                )}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setPage((p) => Math.min(totalPages, p + 1));
                    }}
                    className={safePage >= totalPages ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      )}

      <config.CreateDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreate={handleCreate}
        isCreating={createMutation.isPending}
        organizationId={organizationId}
        ownerProjects={ownerProjects}
        ownerProjectsLoading={ownerProjectsLoading}
        takenProjectIds={takenProjectIds}
      />

      {/* Newly created key reveal dialog */}
      <Dialog
        open={!!newlyCreatedKey}
        onOpenChange={(open) => {
          if (!open) {
            setNewlyCreatedKey('');
            setCreatedKeyLabel('');
            setKeyRevealVisible(false);
          }
        }}
      >
        <DialogContent className="gap-0 sm:max-w-lg">
          <DialogHeader className="space-y-0 text-left">
            <DialogTitle className="text-xl font-bold leading-8">
              {createdKeyLabel}
            </DialogTitle>
            <DialogDescription className="sr-only">
              This API Key will only be shown once. Please copy it now and store it securely.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 flex gap-3 rounded-lg bg-secondary/10 p-4">
            <Key className="mt-0.5 h-6 w-6 shrink-0 text-secondary" aria-hidden />
            <p className="text-sm font-medium leading-snug text-secondary">
              This API Key will only be shown once. Please copy it now and store it securely.
            </p>
          </div>

          <div className="mt-6 space-y-2">
            <FieldLabel htmlFor={`proxima-${config.keyType}-new-api-key-value`}>API Key</FieldLabel>
            <div className="mt-2 flex gap-2">
              <div className="relative min-w-0 flex-1">
                <Input
                  id={`proxima-${config.keyType}-new-api-key-value`}
                  readOnly
                  type={keyRevealVisible ? 'text' : 'password'}
                  value={newlyCreatedKey}
                  className="pr-9! font-mono text-sm"
                  autoComplete="off"
                  spellCheck={false}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer rounded-md p-1"
                  onClick={() => setKeyRevealVisible((v) => !v)}
                  aria-label={keyRevealVisible ? 'Hide API key' : 'Show API key'}
                >
                  {keyRevealVisible ? (
                    <EyeSlash className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0 border-neutral-300 bg-background hover:bg-muted"
                aria-label="Copy API key"
                onClick={async () => {
                  await navigator.clipboard.writeText(newlyCreatedKey);
                  toast.success('Copied to clipboard');
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <DialogFooter className="mt-6 flex-col-reverse gap-2 border-t border-neutral-200 pt-4 sm:flex-row sm:justify-end">
            <Button
              type="button"
              className="bg-teal-900 text-white hover:bg-teal-800 focus-visible:ring-teal-600"
              onClick={() => {
                setNewlyCreatedKey('');
                setCreatedKeyLabel('');
                setKeyRevealVisible(false);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk revoke dialog */}
      <Dialog open={showBulkRevokeDialog} onOpenChange={setShowBulkRevokeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke selected keys?</DialogTitle>
            <DialogDescription>
              This will permanently revoke {selectedCount} API key(s). This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkRevokeDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => bulkRevokeMutation.mutate(Array.from(selectedIds))}
              disabled={bulkRevokeMutation.isPending}
            >
              Revoke keys
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single row revoke dialog */}
      <Dialog open={!!rowToRevoke} onOpenChange={() => setRowToRevoke(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke this key?</DialogTitle>
            <DialogDescription>
              {rowToRevoke?.attributes.name} will be permanently revoked.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRowToRevoke(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (rowToRevoke) {
                  deleteMutation.mutate(rowToRevoke.id, {
                    onSuccess: () => setRowToRevoke(null),
                  });
                }
              }}
              disabled={deleteMutation.isPending}
            >
              Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
